import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatPhoneDisplay, stripPhone } from '../utils/phoneFormat';

// Super admin: one company's text messaging connection (SMS Phase 2), inside
// the company edit modal. Mode, status and assigned number, saved with
// GET/PUT /api/admin/companies/:id/sms. The provisioning
// block (Phase 6a) shows the step and raw error, runs Provision now, and
// releases a SnowTrack-provisioned number once the company name is typed.
//
// It sits inside the modal's subscription <form>, so every button here is
// type="button" and it saves on its own request.
const MODES = [['off', 'Off'], ['provisioned', 'SnowTrack number'], ['byo', 'Own Twilio']];
// The steps Provision now can run (server smsProvisioning.RUNNABLE_STEPS):
// the number steps, then the brand (profile_submitted) and the campaign
// (brand_approved), which in SMS_PROVISIONING=manual only this button runs.
export const RUNNABLE_STEPS = [
  'fee_paid', 'subaccount_created', 'number_purchased', 'service_created', 'profile_submitted', 'brand_approved',
];

const STATUSES = [
  ['not_configured', 'Not configured'], ['pending_registration', 'Registration pending'],
  ['active', 'Active'], ['suspended', 'Suspended'], ['error', 'Error'],
];

// Ten digits, so "(920) 555-0101", "920.555.0101" and "+19205550101" compare equal.
const digitsOf = (phone) => stripPhone(phone).replace(/^1(?=\d{10}$)/, '');

function AdminSmsPanel({ companyId, companyName }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [toast, setToast] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const url = `${process.env.REACT_APP_API_URL}/api/admin/companies/${companyId}/sms`;

  const apply = (d) => {
    setData(d);
    setForm({
      mode: d.mode,
      status: d.status,
      phoneNumber: d.phoneNumber ? formatPhoneDisplay(d.phoneNumber) : '',
    });
  };

  useEffect(() => {
    let live = true;
    axios.get(url)
      .then((res) => { if (live) apply(res.data); })
      .catch((err) => { if (live) setError(err.response?.data?.error || 'Could not load text messaging'); });
    return () => { live = false; };
  }, [url]);

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved('');
    const body = { mode: form.mode, status: form.status };
    // Only an edited number is sent: the server refuses phoneNumber outright on
    // a SnowTrack sub-account (release it instead), and a BYO company's number
    // comes from its own Messaging Service.
    const numberChanged = digitsOf(form.phoneNumber) !== digitsOf(data.phoneNumber);
    if (!data.byo.credentialsStored && numberChanged) body.phoneNumber = form.phoneNumber;
    try {
      const res = await axios.put(url, body);
      apply(res.data);
      // The same toast the staff pages use: it stays on screen while the
      // modal scrolls, and clears itself after five seconds.
      setToast(true);
      setTimeout(() => setToast(false), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save text messaging');
    } finally {
      setSaving(false);
    }
  };

  // Provision now or Release, then reload the connection so the block shows
  // the step the server reached.
  const act = async (path, body, done) => {
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const res = await axios.post(`${url}/${path}`, body);
      const reloaded = await axios.get(url);
      apply(reloaded.data);
      setConfirmName('');
      setSaved(done(res.data));
    } catch (err) {
      setError(err.response?.data?.error || `Could not ${path} the texting number`);
    } finally {
      setBusy(false);
    }
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const p = data && data.provisionAdmin;

  return (
    <fieldset style={{ border: '1px solid var(--border-color, #e0e0e0)', borderRadius: '8px', padding: '12px', marginTop: '16px' }}>
      <legend className="form-label" style={{ padding: '0 4px' }}>Text messaging</legend>
      {error && <div className="alert alert-error">{error}</div>}
      {saved && <div className="alert alert-success">{saved}</div>}
      {toast && (
        <div className="notification-toast notification-success" role="alert">
          <span>Saved.</span>
          <button type="button" onClick={() => setToast(false)} aria-label="Dismiss">&times;</button>
        </div>
      )}
      {!form && !error && <p className="text-muted">Loading...</p>}
      {form && (
        <>
          <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: 0 }}>
            {data.statusText} &middot; {data.outbound30d} sent, {data.optOuts30d} opt-outs in 30 days
            {data.byo.credentialsStored && ` · own Twilio AC...${data.byo.accountSidLast4}`}
          </p>
          <div className="form-group">
            <label className="form-label" htmlFor="admin-sms-mode">Mode</label>
            <select id="admin-sms-mode" className="form-select" value={form.mode} onChange={set('mode')}>
              {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="admin-sms-status">Status</label>
            <select id="admin-sms-status" className="form-select" value={form.status} onChange={set('status')}>
              {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="admin-sms-number">Number</label>
            <input id="admin-sms-number" className="form-input" value={form.phoneNumber} onChange={set('phoneNumber')}
              disabled={data.byo.credentialsStored} placeholder="(920) 555-0100" />
          </div>
          <button type="button" className="btn btn-secondary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save text messaging'}
          </button>
          {p && form.mode === 'provisioned' && (
            <div style={{ borderTop: '1px solid var(--border-color, #e0e0e0)', marginTop: '12px', paddingTop: '12px' }}>
              <p className="form-label" style={{ marginTop: 0 }}>Provisioning</p>
              <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                {p.kind === 'manual' ? 'Manual number (no SnowTrack sub-account)' : `Sub-account AC...${p.subaccountLast4}`}
                {p.serviceLast4 && ` · service MG...${p.serviceLast4}`}
                {` · step ${p.step || 'none'} · ${p.attempts} attempts`}
                {` · setup fee ${p.feePaidAt ? `paid ${new Date(p.feePaidAt).toLocaleDateString()}` : 'not paid'}`}
                {p.releasedAt && ` · released ${new Date(p.releasedAt).toLocaleDateString()}`}
              </p>
              {p.error && <div className="alert alert-error">Provisioning error: {p.error}</div>}
              {p.feePaidAt && !p.releasedAt && RUNNABLE_STEPS.includes(p.step) && (
                <button type="button" className="btn btn-secondary" disabled={busy}
                  onClick={() => act('provision', {}, (r) => (r.phoneNumber
                    ? `Provisioned: ${formatPhoneDisplay(r.phoneNumber)}.` : `Provisioning reached ${r.step || 'no step'}.`))}>
                  {busy ? 'Working...' : 'Provision now'}
                </button>
              )}
              {p.kind === 'subaccount' && !p.releasedAt && (
                <div className="form-group" style={{ marginTop: '12px' }}>
                  <label className="form-label" htmlFor="admin-sms-release">
                    Release: type {companyName ? `"${companyName}"` : "the company's name"} to confirm. Immediate.
                  </label>
                  <input id="admin-sms-release" className="form-input" value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)} autoComplete="off" />
                  <button type="button" className="btn btn-danger" style={{ marginTop: '8px' }}
                    disabled={busy || !confirmName.trim()}
                    onClick={() => act('release', { confirmName }, () => 'Texting number released.')}>
                    Release number
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </fieldset>
  );
}

export default AdminSmsPanel;
